import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment.local';

export interface SecuritySession {
  sessionId: string;
  sessionKey: string;
  issuedAt: number;
  scope: string;
}

/**
 * Guarda a sessão efêmera (sessionId + sessionKey) EM MEMÓRIA e faz o
 * bootstrap trocando o token do encrypt por uma sessão em /security/session.
 * Idempotente: chamadas repetidas com o mesmo token reaproveitam o bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class CredentialService {
  private session: SecuritySession | null = null;
  private bootstrapPromise: Promise<void> | null = null;
  private bootstrappedToken: string | null = null;

  constructor(private http: HttpClient) {}

  /** Troca o token do encrypt por uma sessão. Chame uma vez ao entrar na jornada. */
  bootstrap(encryptToken: string): Promise<void> {
    if (!environment.security?.enabled || !encryptToken) {
      return Promise.resolve();
    }
    if (this.bootstrapPromise && this.bootstrappedToken === encryptToken) {
      return this.bootstrapPromise;
    }
    this.bootstrappedToken = encryptToken;
    this.bootstrapPromise = this.requestSession(encryptToken);
    return this.bootstrapPromise;
  }

  /** Resolve quando o bootstrap em andamento terminar (ou imediatamente se não houver). */
  whenReady(): Promise<void> {
    return this.bootstrapPromise ?? Promise.resolve();
  }

  current(): SecuritySession | null {
    return this.session;
  }

  clear(): void {
    this.session = null;
    this.bootstrapPromise = null;
    this.bootstrappedToken = null;
  }

  private async requestSession(encryptToken: string): Promise<void> {
    const url = `${this.sessionEndpoint()}?token=${encodeURIComponent(encryptToken)}`;
    try {
      this.session = await firstValueFrom(this.http.get<SecuritySession>(url));
    } catch {
      // Falha no bootstrap não pode quebrar o app: segue sem sessão (request passa sem assinatura).
      this.session = null;
      this.bootstrappedToken = null;
    }
  }

  /** Deriva a raiz do contexto (apiBase/context) a partir de uma URL já configurada. */
  private sessionEndpoint(): string {
    const root = environment.apiUrls.economicGroupSimulation.replace(/\/economic-group-simulation$/, '');
    return `${root}/security/session`;
  }
}
